import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { storage } from '../../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { grantSeasonSixCodeAccess, hasSeasonSixAccess, isSeasonSixResource, validateSeasonSixCode } from '../../utils/seasonSixAccess';
import { millionairesConnectLessonResources } from '../../data/economyPrograms';
import EmbeddedVideoCard from '../../components/domain/EmbeddedVideoCard';
import {
  loadMillionairesConnectProgress,
  persistMillionairesConnectProgress
} from '../../utils/courseProgress';
import { isSequentialLessonUnlocked } from '../../utils/lessonSequence';
import {
  isMillionairesConnectCourse,
  resourceMatchesActiveCourse
} from '../../utils/resourceAccess';
import { getResourceFolder } from '../../utils/seasonContent';

const Icons = {
  Video: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="23 7 16 12 23 17 23 7"/>
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
    </svg>
  ),
  Download: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Book: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  ),
  Headphones: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
    </svg>
  ),
  Lock: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  Unlock: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 9-3"/>
    </svg>
  ),
  Users: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Play: ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  )
};

const getYoutubeThumbnail = (embedUrl = '') => {
  const match = String(embedUrl).match(/embed\/([^?&]+)/i);
  if (!match?.[1]) return '';
  return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
};

export default function StudentClassroom() {
  const location = useLocation();
  const { user } = useAuth();
  const { resources, courses = [] } = useData();
  const [viewMode, setViewMode] = useState('all');
  const [activeResource, setActiveResource] = useState(null);
  const [resourceUrl, setResourceUrl] = useState('');
  const [resourceError, setResourceError] = useState('');
  const [resourceLoading, setResourceLoading] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeError, setAccessCodeError] = useState('');
  const [openResourceFolders, setOpenResourceFolders] = useState(() => new Set(['Season 7', 'Season 6']));
  const [seasonSixGranted, setSeasonSixGranted] = useState(() => hasSeasonSixAccess(user));
  const [completedLessonIds, setCompletedLessonIds] = useState(() => new Set());
  const [progressLoading, setProgressLoading] = useState(true);
  const handledLessonHashRef = useRef('');
  const activeCourseId = user?.courseId;
  const activeIntake = user?.intake;
  const seasonSixUnlocked = Boolean(user?.lmsActive || user?.accessStatus === 'paid' || seasonSixGranted);
  const millionairesConnectUnlocked = isMillionairesConnectCourse(courses, activeCourseId, activeIntake);
  const activeCourse = courses.find(course => course.id === activeCourseId);

  const scopedResources = resources.filter(resource => {
    return resourceMatchesActiveCourse({
      resource,
      courses,
      activeCourseId,
      activeIntake
    });
  });

  const millionairesConnectLibrary = useMemo(() => {
    return millionairesConnectLessonResources;
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!millionairesConnectUnlocked) {
      setCompletedLessonIds(new Set());
      setProgressLoading(false);
      return undefined;
    }

    setProgressLoading(true);
    loadMillionairesConnectProgress(user?.id).then((completedIds) => {
      if (cancelled) return;
      const knownIds = new Set(millionairesConnectLibrary.map((lesson) => lesson.lessonId));
      setCompletedLessonIds(new Set(completedIds.filter((id) => knownIds.has(id))));
      setProgressLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [millionairesConnectLibrary, millionairesConnectUnlocked, user?.id]);

  const isLessonUnlocked = useCallback((resource) => {
    if (!resource?.isMillionairesConnectVideo) return true;
    return isSequentialLessonUnlocked({
      lessons: millionairesConnectLibrary,
      lessonIndex: resource.sequenceIndex,
      completedLessonIds,
      hasAccess: seasonSixUnlocked
    });
  }, [completedLessonIds, millionairesConnectLibrary, seasonSixUnlocked]);

  useEffect(() => {
    const lessonId = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    if (!lessonId || progressLoading || handledLessonHashRef.current === lessonId) return;
    handledLessonHashRef.current = lessonId;
    const requestedLesson = millionairesConnectLibrary.find((lesson) => lesson.lessonId === lessonId);
    if (!requestedLesson || !isLessonUnlocked(requestedLesson)) return;
    setActiveResource(requestedLesson);
    setResourceUrl(requestedLesson.embedUrl);
    setResourceError('');
  }, [isLessonUnlocked, location.hash, millionairesConnectLibrary, progressLoading]);

  const completedLessonCount = millionairesConnectLibrary.filter((lesson) => (
    completedLessonIds.has(lesson.lessonId)
  )).length;
  const courseProgressPercent = millionairesConnectLibrary.length
    ? Math.round((completedLessonCount / millionairesConnectLibrary.length) * 100)
    : 0;

  const seasonSevenResources = scopedResources.filter((resource) => getResourceFolder(resource) === 'Season 7');
  const classroomResources = millionairesConnectUnlocked
    ? [...seasonSevenResources, ...millionairesConnectLibrary]
    : scopedResources;

  const filteredResources = viewMode === 'all' 
    ? classroomResources
    : classroomResources.filter(r => {
        if (viewMode === 'video') return r.type === 'video';
        if (viewMode === 'pdf') return r.type === 'pdf' || r.type === 'epub';
        if (viewMode === 'document') return ['word', 'excel', 'powerpoint', 'file'].includes(r.type);
        return true;
      });

  const resourceFolderGroups = useMemo(() => {
    const groups = new Map();
    filteredResources.forEach((resource) => {
      const folder = getResourceFolder(resource);
      if (!groups.has(folder)) groups.set(folder, []);
      groups.get(folder).push(resource);
    });
    return Array.from(groups.entries());
  }, [filteredResources]);

  const toggleResourceFolder = (folder) => {
    setOpenResourceFolders((current) => {
      const next = new Set(current);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video': return 'var(--primary)';
      case 'event': return 'var(--danger)';
      case 'epub': return 'var(--gold)';
      case 'audiobook': return 'var(--success)';
      default: return 'var(--gray-500)';
    }
  };

  const formatEventDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  const formatEventWindow = (startValue, endValue) => {
    const startLabel = formatEventDate(startValue);
    const endLabel = formatEventDate(endValue);

    if (startLabel && endLabel) return `${startLabel} to ${endLabel}`;
    return startLabel || endLabel || '';
  };

  const handleMillionairesVideoEnded = useCallback((resource) => {
    if (!resource?.lessonId) return;
    setCompletedLessonIds((current) => {
      const next = new Set(current);
      next.add(resource.lessonId);
      persistMillionairesConnectProgress(user?.id, [...next]);
      return next;
    });
  }, [user?.id]);

  const renderResourcePreview = () => {
    if (resourceLoading) return <div style={{ color: 'var(--gray-500)' }}>Loading resource...</div>;
    if (!resourceUrl) return <div style={{ color: 'var(--gray-500)' }}>{resourceError || 'Unable to load this file.'}</div>;

    if (activeResource?.type === 'event') {
      if (activeResource?.embedUrl) {
        return (
          <iframe
            title={activeResource.title}
            src={resourceUrl}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        );
      }
      if ((activeResource?.mimeType || '').startsWith('image/')) {
        return <img alt={activeResource.title} src={resourceUrl} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />;
      }
      if ((activeResource?.mimeType || '').startsWith('video/')) {
        return <video src={resourceUrl} controls style={{ width: '100%', maxHeight: '70vh' }} />;
      }
    }

    return (
      <iframe
        title={activeResource.title}
        src={activeResource?.type === 'pdf' ? `${resourceUrl}#toolbar=0&navpanes=0&scrollbar=0` : resourceUrl}
        style={{ width: '100%', height: '70vh', border: 'none' }}
      />
    );
  };

  const handleOpenResource = async (resource, options = {}) => {
    if (!resource) return;
    if (resource.isMillionairesConnectVideo && seasonSixUnlocked && !isLessonUnlocked(resource)) {
      setResourceError(`Complete lesson ${resource.sequenceNumber - 1} before opening this lesson.`);
      return;
    }
    const seasonSixLocked = resource.type === 'video' && isSeasonSixResource(resource) && !seasonSixUnlocked;
    if (seasonSixLocked && !options.skipLock) {
      setActiveResource(resource);
      setResourceUrl('');
      setAccessCode('');
      setAccessCodeError('');
      setResourceError('Season 6 videos are locked. Complete payment or enter the access code to unlock them.');
      return;
    }
    if (resource.isPaid && !user?.lmsActive && !seasonSixUnlocked && !options.skipLock) {
      setActiveResource(resource);
      setResourceUrl('');
      setAccessCode('');
      setAccessCodeError('');
      setResourceError('This is a premium resource. Please complete payment to unlock access.');
      return;
    }
    setResourceLoading(true);
    setActiveResource(resource);
    setResourceError('');
    try {
      if (resource.embedUrl) {
        setResourceUrl(resource.embedUrl);
      } else if (resource.storagePath) {
        const fileRef = ref(storage, resource.storagePath);
        const url = await getDownloadURL(fileRef);
        setResourceUrl(url);
      } else {
        setResourceError('Unable to load this resource.');
      }
    } catch (err) {
      console.error('Failed to load resource', err);
      setResourceError('Unable to load this file. Please try again.');
    } finally {
      setResourceLoading(false);
    }
  };

  const handleSeasonSixUnlock = async () => {
    if (!validateSeasonSixCode(accessCode)) {
      setAccessCodeError('Invalid access code. Please try again.');
      return;
    }
    grantSeasonSixCodeAccess();
    setSeasonSixGranted(true);
    setAccessCodeError('');
    if (activeResource) {
      const nextRequiredLesson = activeResource.isMillionairesConnectVideo
        ? millionairesConnectLibrary.find((lesson) => !completedLessonIds.has(lesson.lessonId)) || millionairesConnectLibrary[0]
        : activeResource;
      await handleOpenResource(nextRequiredLesson, { skipLock: true });
    }
  };

  const handleCloseResource = () => {
    setResourceUrl('');
    setActiveResource(null);
    setResourceError('');
  };

  return (
    <div className="lms-classroom">
      <Link to={`/student/courses/${activeCourseId || ''}`} className="lms-back-link">← Course Overview</Link>
      <div className="page-header">
        <p className="eyebrow">{activeCourse?.code || 'Learning'}</p>
        <h1 className="page-title">{activeCourse?.title || (millionairesConnectUnlocked ? 'Millionaires Connect Resources' : 'Course Learning')}</h1>
        <p className="page-subtitle">
          {[activeCourse?.faculty, activeIntake].filter(Boolean).join(' • ') || 'Access your videos, PDFs and learning materials.'}
        </p>
      </div>

      <nav className="lms-course-tabs" aria-label="Course navigation"><Link to={`/student/courses/${activeCourseId || ''}`}>Overview</Link><Link className="active" to="/student/classroom">Learning</Link><Link to="/student/assignments">Assignments</Link><Link to="/student/resources">Resources</Link><Link to="/student/grades">Grades</Link></nav>

      {/* Info Banner */}
      <div className="card" style={{ marginBottom: '24px', background: 'var(--primary)' }}>
        <div className="card-body" style={{ color: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              background: 'rgba(255,255,255,0.2)', 
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icons.Users />
            </div>
            <div>
              <h3 style={{ fontSize: '1rem', marginBottom: '4px', color: 'var(--gold)' }}>
                Watch Together & Discuss
              </h3>
              <p style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                Form a study group with friends and watch sessions together. Share insights and discuss in your community.
              </p>
              <div style={{ marginTop: '12px' }}>
                <a href="/student/community" className="btn btn-outline btn-sm" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}>
                  Open Community
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="lms-classroom-filters" style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'video', label: 'Videos' },
          { key: 'pdf', label: 'PDFs' },
          { key: 'document', label: 'Documents' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setViewMode(tab.key)}
            className={`btn ${viewMode === tab.key ? 'btn-primary' : 'btn-outline'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Resources Folders */}
      <div style={{ display: 'grid', gap: '24px' }}>
        {filteredResources.length === 0 && (
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-body" style={{ textAlign: 'center', color: 'var(--gray-500)' }}>
              No study materials available for this intake yet.
              {millionairesConnectUnlocked ? ' Your Millionaires Connect resources will appear here as soon as they are uploaded.' : ''}
            </div>
          </div>
        )}
        {resourceFolderGroups.map(([folder, folderResources]) => (
          <section key={folder} className="card" style={{ overflow: 'hidden' }}>
            <button
              type="button"
              className="card-header"
              onClick={() => toggleResourceFolder(folder)}
              aria-expanded={openResourceFolders.has(folder)}
              style={{ width: '100%', border: 0, textAlign: 'left', cursor: 'pointer', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}
            >
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gold-dark)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Resource folder
                </div>
                <h2 style={{ fontSize: '1.25rem', marginTop: '4px' }}>{folder}</h2>
                <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                  {folder === 'Season 6' ? 'Previous-season video library' : 'Current Season 7 learning materials'}
                </p>
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                <span className="badge badge-gold">{folderResources.length} {folderResources.length === 1 ? 'item' : 'items'}</span>
                <span className="btn btn-outline btn-sm">{openResourceFolders.has(folder) ? 'Close folder' : 'Open folder'}</span>
              </span>
            </button>
            {openResourceFolders.has(folder) && (
              <div className="mc-folder-content">
                {folder === 'Season 6' && millionairesConnectUnlocked && (
                  <div className="mc-progress-panel">
                    <div className="mc-progress-copy">
                      <span>Millionaires Connect progress</span>
                      <strong>{progressLoading ? 'Loading progress…' : `${completedLessonCount} of ${millionairesConnectLibrary.length} lessons complete`}</strong>
                      <p>Complete each video to unlock the next lesson. Finished lessons remain available to rewatch.</p>
                    </div>
                    <div className="mc-progress-meter" aria-label={`${courseProgressPercent}% complete`}>
                      <span style={{ width: `${courseProgressPercent}%` }} />
                    </div>
                    <b>{courseProgressPercent}%</b>
                  </div>
                )}
                <div className="mc-resource-grid">
                  {folderResources.map((resource) => {
                    const lessonUnlocked = isLessonUnlocked(resource);
                    const lessonComplete = resource.lessonId && completedLessonIds.has(resource.lessonId);
                    const sequenceLocked = resource.isMillionairesConnectVideo && seasonSixUnlocked && !lessonUnlocked;
                    const unavailable = !resource.storagePath && !resource.embedUrl;
                    return (
                      <React.Fragment key={resource.id}>
                      <div id={resource.lessonId || undefined} className={`card mc-resource-card${sequenceLocked ? ' is-locked' : ''}${lessonComplete ? ' is-complete' : ''}`}>
                        <div className="mc-resource-media" style={{ background: getTypeColor(resource.type) }}>
                          {resource.type === 'video' && getYoutubeThumbnail(resource.embedUrl) ? (
                            <img src={getYoutubeThumbnail(resource.embedUrl)} alt="" />
                          ) : (
                            <div className="mc-resource-placeholder">
                              {resource.type === 'video' && <Icons.Video size={32} />}
                              {resource.type === 'event' && <Icons.Video size={32} />}
                              {resource.type === 'audiobook' && <Icons.Headphones size={32} />}
                              {resource.type === 'epub' && <Icons.Book size={32} />}
                              <span>{resource.type}</span>
                            </div>
                          )}
                          {resource.isMillionairesConnectVideo && (
                            <span className="mc-lesson-number">Lesson {resource.sequenceNumber}</span>
                          )}
                          {lessonComplete ? (
                            <span className="mc-resource-state is-complete"><Icons.Unlock /> Completed</span>
                          ) : sequenceLocked ? (
                            <span className="mc-resource-state"><Icons.Lock /> Locked</span>
                          ) : resource.type === 'video' && seasonSixUnlocked ? (
                            <span className="mc-resource-state is-open"><Icons.Unlock /> Available</span>
                          ) : null}
                        </div>
                        <div className="card-body mc-resource-body">
                          <div className="mc-resource-module">{resource.module}</div>
                          <h3>{resource.title}</h3>
                          <div className="mc-resource-meta">
                            {resource.isMillionairesConnectVideo
                              ? `Video lesson ${resource.sequenceNumber} of ${resource.sequenceTotal}`
                              : resource.type === 'event'
                                ? `Event: ${formatEventWindow(resource.eventStartDate || resource.eventDate, resource.eventEndDate)}`
                                : resource.type === 'video'
                                  ? `Duration: ${resource.duration || 'Streaming'}`
                                  : resource.type === 'audiobook'
                                    ? `Duration: ${resource.duration || 'Audio'}`
                                    : `Size: ${resource.size}`}
                            {resource.hasWorkbook && ' • Includes Workbook'}
                            {resource.downloadable === false && ' • View only'}
                          </div>
                          {sequenceLocked && (
                            <p className="mc-lock-instruction">Complete lesson {resource.sequenceNumber - 1} to unlock.</p>
                          )}
                          <div className="mc-resource-actions">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleOpenResource(resource)}
                              disabled={unavailable || sequenceLocked}
                            >
                              {sequenceLocked ? <Icons.Lock /> : <Icons.Play />}
                              {sequenceLocked
                                ? 'Locked'
                                : lessonComplete
                                  ? 'Rewatch'
                                  : resource.type === 'video' && isSeasonSixResource(resource) && !seasonSixUnlocked
                                    ? 'Unlock / Watch'
                                    : resource.type === 'video'
                                      ? 'Watch lesson'
                                      : resource.type === 'event'
                                        ? 'Open Poster'
                                        : 'Open'}
                            </button>
                            {resource.type === 'event' && resource.eventLink && (
                              <a className="btn btn-outline btn-sm" href={resource.eventLink} target="_blank" rel="noreferrer">Register</a>
                            )}
                            {resource.hasWorkbook && (
                              <button className="btn btn-outline btn-sm"><Icons.Download /> Workbook</button>
                            )}
                          </div>
                        </div>
                      </div>
                      {resource.type === 'video' && activeResource?.id === resource.id && resourceUrl && (
                        <div className="mc-inline-player-wrap">
                          <EmbeddedVideoCard
                            resource={activeResource}
                            sourceUrl={resourceUrl}
                            lessons={millionairesConnectLibrary}
                            completedLessonIds={completedLessonIds}
                            hasAccess={seasonSixUnlocked}
                            onComplete={handleMillionairesVideoEnded}
                            onSelectLesson={handleOpenResource}
                            onClose={handleCloseResource}
                          />
                        </div>
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        ))}
      </div>

      {activeResource && (activeResource.type !== 'video' || !resourceUrl) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            zIndex: 2000
          }}
        >
          <div style={{ background: 'white', borderRadius: '16px', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', gap: '12px' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{activeResource.title}</div>
                {activeResource.type === 'event' && formatEventWindow(activeResource.eventStartDate || activeResource.eventDate, activeResource.eventEndDate) && (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                    {formatEventWindow(activeResource.eventStartDate || activeResource.eventDate, activeResource.eventEndDate)}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {activeResource.type === 'event' && activeResource.eventLink && (
                  <a className="btn btn-outline btn-sm" href={activeResource.eventLink} target="_blank" rel="noreferrer">
                    Register
                  </a>
                )}
                {activeResource.downloadable && resourceUrl && (
                  <a className="btn btn-outline btn-sm" href={resourceUrl} download>
                    <Icons.Download /> Download
                  </a>
                )}
                <button className="btn btn-outline btn-sm" onClick={handleCloseResource}>Close</button>
              </div>
            </div>
            <div style={{ padding: '16px', minHeight: '60vh' }}>
              {activeResource?.type === 'video' && isSeasonSixResource(activeResource) && !seasonSixUnlocked && !resourceUrl ? (
                <div style={{ display: 'grid', gap: '16px', maxWidth: '420px' }}>
                  <div style={{ color: 'var(--gray-600)' }}>
                    {resourceError || 'Season 6 videos are locked.'}
                  </div>
                  <input
                    className="form-input"
                    placeholder="Enter access code"
                    value={accessCode}
                    onChange={(event) => setAccessCode(event.target.value)}
                  />
                  {accessCodeError && <div className="badge badge-danger">{accessCodeError}</div>}
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button className="btn btn-primary btn-sm" onClick={handleSeasonSixUnlock}>
                      Unlock with Code
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => window.location.assign('/student/registration/checkout')}>
                      Complete Payment
                    </button>
                  </div>
                </div>
              ) : (
                renderResourcePreview()
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resource Info */}
      <div className="card" style={{ marginTop: '32px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Your Resources</h3>
        </div>
        <div className="card-body">
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
            <div style={{ textAlign: 'center', padding: '24px', background: 'var(--gray-50)', borderRadius: '12px' }}>
              <div style={{ marginBottom: '8px', color: 'var(--primary)' }}>
                <Icons.Book size={32} />
              </div>
              <h4 style={{ marginBottom: '4px' }}>eBooks</h4>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                Included in your tuition
              </p>
            </div>
            <div style={{ textAlign: 'center', padding: '24px', background: 'var(--gray-50)', borderRadius: '12px' }}>
              <div style={{ marginBottom: '8px', color: 'var(--success)' }}>
                <Icons.Headphones size={32} />
              </div>
              <h4 style={{ marginBottom: '4px' }}>Audiobooks</h4>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                Listen anywhere, anytime
              </p>
            </div>
            <div style={{ textAlign: 'center', padding: '24px', background: 'var(--gray-50)', borderRadius: '12px' }}>
              <div style={{ marginBottom: '8px', color: 'var(--gold-dark)' }}>
                <Icons.Book size={32} />
              </div>
              <h4 style={{ marginBottom: '4px' }}>Paperbacks</h4>
              <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                Order at discounted price
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
